import React, { useEffect } from 'react';
import { Card } from 'src/components/Card';
import { CardIndicator, CardIndicatorProps } from 'src/components/CardIndicator';
import { Col } from 'src/components/Col';
import { Row } from 'src/components/Row';
import { getSimpleMinutesAndSeconds } from 'src/utils/helpers';
import { useSelector } from 'react-redux';
import { DoD } from 'src/interfaces/graph.interface';
import { PlotLineArea } from '../../PlotLineArea';

type IndicatorType = Omit<CardIndicatorProps, 'className'> & {
  data: any[];
  plotName?: string;
};

const CameraIndicators: React.FC = () => {
  const [indicators, setIndicators] = React.useState<IndicatorType[]>([]);
  const { cameraStats, cameraStatsLoading } = useSelector((state: any) => state.cameras);

  const { allCameraGraphs, allCameraGraphsLoading } = useSelector((state: any) => state.graphs);
  const { all_customers_avg, graph_data = [], day_over_day_data = [] } = allCameraGraphs || {};

  useEffect(() => {
    const { footfall_percentage } = cameraStats;
    const graphs = Array.isArray(graph_data) ? graph_data : [];
    const { data: totalFootfallGraph = [] } =
      graphs.find((graph: any) => graph.name === 'cam_total_footfall_graph') || {};
    const { data: avgTimeGraph = [] } = graphs.find((graph: any) => graph.name === 'cam_average_time_graph') || {};

    const camDoD = ((): DoD => {
      const values: DoD = {
        footfallDoD: {
          percentage: '0',
          icon: 'empty',
        },
        avgTimeDoD: {
          percentage: '0',
          icon: 'empty',
        },
      };
      if (Array.isArray(day_over_day_data) && day_over_day_data.length > 0) {
        const dataFootfall =
          day_over_day_data.find((graph: any) => graph[0] === 'store_day_over_day_footfall_graph') || [];
        const dataAvgTime =
          day_over_day_data.find((graph: any) => graph[0] === 'store_day_over_day_average_time_graph') || [];
        const footfallPercentage = dataFootfall[1]?.percentage;
        const avgTimePercentage = dataAvgTime[1]?.percentage;
        return {
          footfallDoD:
            typeof footfallPercentage === 'number'
              ? {
                percentage: footfallPercentage.toFixed(0),
                icon: footfallPercentage > 0 ? 'up' : 'down',
              }
              : values.footfallDoD,
          avgTimeDoD:
            typeof avgTimePercentage === 'number'
              ? {
                percentage: avgTimePercentage.toFixed(0),
                icon: avgTimePercentage > 0 ? 'up' : 'down',
              }
              : values.avgTimeDoD,
        };
      }
      return values;
    })();
    const newIndicators = [
      {
        plotName: 'footfall',
        label: 'Shoppers By Camera',
        tooltip:
          "Percentage of shoppers seen in this camera out of the Total Footfall in Store by user's selected dates and hours.",
        bottom: {},
        // Shows 100% for canary wharf camera 25 (cam. 3).
        value: footfall_percentage,
        icon: camDoD.footfallDoD.icon,
        percent: camDoD.footfallDoD.percentage,
        data: [
          {
            name: 'shoppers_by_camera',
            y: totalFootfallGraph.length === 1 ? [...totalFootfallGraph, 0] : totalFootfallGraph,
            hoverinfo: 'none',
            fill: 'tozeroy',
            fillcolor: 'rgba(83, 194, 197, 0.1)',
            line: { color: '#53D2C5' },
            mode: 'lines',
          },
        ],
      },
      {
        plotName: 'avg',
        label: 'Average Visit Time',
        tooltip: "Average Time people have spent in this camera's view angle at user selected dates and hours.",
        icon: camDoD.avgTimeDoD.icon,
        percent: camDoD.avgTimeDoD.percentage,
        bottom: {},
        value: all_customers_avg ? getSimpleMinutesAndSeconds(all_customers_avg) : '0',
        data: [
          {
            name: 'average_visit_time',
            y: avgTimeGraph?.length === 1 ? [...avgTimeGraph, 0] : avgTimeGraph,
            hoverinfo: 'none',
            fill: 'tozeroy',
            fillcolor: 'rgba(83, 97, 255, 0.1)',
            line: { color: '#5361ff' },
            mode: 'lines',
          },
        ],
      },
    ];
    setIndicators(newIndicators);
  }, [all_customers_avg, cameraStats, day_over_day_data, graph_data]);

  if (allCameraGraphsLoading || cameraStatsLoading) {
    return (
      <div className="animate-pulse flex flex-row gap-3 my-3">
        <div role="status" className="w-[50%] bg-white p-4 rounded-lg shadow flex flex-row gap-2">
          <div className="h-24 w-32 bg-gray-200 rounded-lg br-5 mb-2.5" />
          <div className="h-24 w-full bg-gray-200 rounded-lg br-5 mb-2.5" />
        </div>
        <div role="status" className="w-[50%] bg-white p-4 rounded-lg shadow flex flex-row gap-2">
          <div className="h-24 w-32 bg-gray-200 rounded-lg br-5 mb-2.5" />
          <div className="h-24 w-full bg-gray-200 rounded-lg br-5 mb-2.5" />
        </div>
      </div>
    );
  }

  return (
    <Row>
      {indicators.map((item, idx) => (
        <Col size="6" key={`indicator-${item.value}-${idx + 1}`}>
          <Card className="flex-1 justify-between">
            <CardIndicator {...item} toggle={item.toggle}>
              <PlotLineArea data={item.data} plotName={item.plotName} />
            </CardIndicator>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default CameraIndicators;
